import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import {
  useCreateFolderMutation,
  useUpdateFolderMutation,
} from './hooks/folderMutations';
import { inputStringLength } from 'assets/data/config';
import FormTextField from '../IAP/formFields/FormTextField';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { FileOrFolder, FileScopeType } from './hooks/useGetAllFiles';
import RosterPositionTagField, {
  type RosterPositionTag,
} from './RosterPositionTagField';
import AorFolderTagField from './AorFolderTagField';
import {
  useEpaFlag,
  useHwcgFlag,
  useUscgFocRequirementsFlag,
} from 'utils/featureFlags';

export type AddFolderDialogProps = {
  onSuccess: (data?: any) => void;
  onClose: () => void;
  dialogType: 'Create' | 'Edit';
  folderToEdit?: FileOrFolder;
  ownerId: string;
  scopeType?: FileScopeType;
};

type FormProps = {
  title: string;
};

const AddEditFolderDialog: React.FC<AddFolderDialogProps> = ({
  onSuccess,
  onClose,
  dialogType,
  folderToEdit,
  ownerId,
  scopeType,
}) => {
  const form = useForm<FormProps>({
    defaultValues: { title: folderToEdit?.folder_name || '' },
  });
  const { handleSubmit } = form;

  const hwcg = useHwcgFlag();
  const [positionTag, setPositionTag] = useState<RosterPositionTag>({
    positionName:
      folderToEdit?.type === 'folder'
        ? (folderToEdit.position_name ?? null)
        : null,
    positionTemplateSlug:
      folderToEdit?.type === 'folder'
        ? (folderToEdit.position_template_slug ?? null)
        : null,
  });

  const epa = useEpaFlag();
  const uscgFoc = useUscgFocRequirementsFlag();
  // FEMA regions stand in for AORs under EPA; they have no saved ids to tag with.
  const aorTaggable = scopeType === 'organization' && !(epa && !uscgFoc);
  const [aorId, setAorId] = useState<string | null>(
    folderToEdit?.type === 'folder' ? (folderToEdit.aor_id ?? null) : null
  );

  const createFolderMutation = useCreateFolderMutation(onSuccess);
  const updateFolderMutation = useUpdateFolderMutation(onSuccess);

  const onSubmit = (data: FormProps) => {
    const payload = {
      name: data.title,
      workspaceId: ownerId,
      ...(scopeType ? { scopeType } : {}),
      ...(aorTaggable ? { aorId } : {}),
      ...(hwcg
        ? {
            positionName: positionTag.positionName,
            positionTemplateSlug: positionTag.positionName
              ? positionTag.positionTemplateSlug
              : null,
          }
        : {}),
    };
    if (dialogType === 'Create') {
      createFolderMutation.mutate(payload);
    } else if (dialogType === 'Edit' && folderToEdit?.id) {
      updateFolderMutation.mutate({ ...payload, folderId: folderToEdit.id });
    }
  };

  return (
    <Modal
      show={true}
      onHide={onClose}
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton closeVariant="white">
        <Modal.Title>{dialogType} Folder</Modal.Title>
      </Modal.Header>
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Modal.Body>
            <FormTextField
              title="Name"
              fieldName="title"
              rules={{
                required: { value: true, message: 'This is a required field' },
                maxLength: {
                  value: inputStringLength,
                  message: `Maximum ${inputStringLength} characters allowed`,
                },
              }}
            />
            {hwcg ? (
              <RosterPositionTagField
                ownerId={ownerId}
                scopeType={scopeType}
                value={positionTag}
                onChange={setPositionTag}
              />
            ) : null}
            {aorTaggable ? (
              <AorFolderTagField value={aorId} onChange={setAorId} />
            ) : null}
          </Modal.Body>
          <Modal.Footer>
            <div className="button-group">
              <StylishNewButton
                className="button--secondary button--reverse"
                onClick={onClose}
                type="button"
              >
                Cancel
              </StylishNewButton>
              <StylishNewButton
                className="button--primary"
                type="submit"
                disabled={
                  createFolderMutation.isPending ||
                  updateFolderMutation.isPending
                }
              >
                Save
              </StylishNewButton>
            </div>
          </Modal.Footer>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default AddEditFolderDialog;
